import { Controller } from "@hotwired/stimulus"

/**
 * Stimulus controller for handling dynamic audience selection in communications.
 * Manages the visibility of recipient options based on audience type (parents/staff)
 * and the specific targeting option (all, by class, specific, by role).
 */
export default class extends Controller {
  static targets = [
    "audienceRadio",
    "sendToWhomRadio",
    "sendToWhomWrapper",
    "parentOptions",
    "staffOptions",
    "parentsFields",
    "staffFields",
    "classrooms",
    "cycles",
    "levels",
    "students",
    "allParentsMessage",
    "roles",
    "specificStaff",
    "allStaffMessage"
  ]

  connect() {
    // Initialize visibility based on current values
    this.updateVisibility()
  }

  /**
   * Called when the audience type changes (parents vs staff)
   * Auto-selects default send_to_whom option for the new audience
   */
  audienceChanged(event) {
    const audience = event.target.value
    this.autoSelectDefault(audience)
    this.updateVisibility()
  }

  /**
   * Called when the send_to_whom option changes
   */
  sendToWhomChanged(event) {
    this.updateRecipientContainers()
  }

  /**
   * Updates all visibility states based on current selections
   */
  updateVisibility() {
    const selectedAudience = this.getSelectedAudience()

    // Show/hide parent vs staff options in send_to_whom section
    if (this.hasParentOptionsTarget && this.hasStaffOptionsTarget) {
      this.parentOptionsTarget.classList.toggle('hidden', selectedAudience !== 'school_parents')
      this.staffOptionsTarget.classList.toggle('hidden', selectedAudience !== 'school_staff')
    }

    // Show/hide parent vs staff fields in recipients section
    if (this.hasParentsFieldsTarget && this.hasStaffFieldsTarget) {
      this.parentsFieldsTarget.classList.toggle('hidden', selectedAudience !== 'school_parents')
      this.staffFieldsTarget.classList.toggle('hidden', selectedAudience !== 'school_staff')
    }

    // Update recipient containers based on current send_to_whom value
    this.updateRecipientContainers()
  }

  /**
   * Updates the visibility of recipient containers based on send_to_whom value
   */
  updateRecipientContainers() {
    const sendToWhom = this.getSelectedSendToWhom()

    // Hide all containers first
    this.hideAllContainers()

    // Show the appropriate container
    switch (sendToWhom) {
      case 'class_parents':
        if (this.hasClassroomsTarget) {
          this.classroomsTarget.classList.remove('hidden')
        }
        break
      case 'cycle_parents':
        if (this.hasCyclesTarget) {
          this.cyclesTarget.classList.remove('hidden')
        }
        break
      case 'level_parents':
        if (this.hasLevelsTarget) {
          this.levelsTarget.classList.remove('hidden')
        }
        break
      case 'specific_parents':
        if (this.hasStudentsTarget) {
          this.studentsTarget.classList.remove('hidden')
        }
        break
      case 'all_parents':
        if (this.hasAllParentsMessageTarget) {
          this.allParentsMessageTarget.classList.remove('hidden')
        }
        break
      case 'by_role':
        if (this.hasRolesTarget) {
          this.rolesTarget.classList.remove('hidden')
        }
        break
      case 'specific_staff':
        if (this.hasSpecificStaffTarget) {
          this.specificStaffTarget.classList.remove('hidden')
        }
        break
      case 'all_staff':
        if (this.hasAllStaffMessageTarget) {
          this.allStaffMessageTarget.classList.remove('hidden')
        }
        break
    }
  }

  /**
   * Hides all recipient containers
   */
  hideAllContainers() {
    // Parent containers
    if (this.hasClassroomsTarget) {
      this.classroomsTarget.classList.add('hidden')
    }
    if (this.hasStudentsTarget) {
      this.studentsTarget.classList.add('hidden')
    }
    if (this.hasCyclesTarget) {
      this.cyclesTarget.classList.add('hidden')
    }
    if (this.hasLevelsTarget) {
      this.levelsTarget.classList.add('hidden')
    }
    if (this.hasAllParentsMessageTarget) {
      this.allParentsMessageTarget.classList.add('hidden')
    }

    // Staff containers
    if (this.hasRolesTarget) {
      this.rolesTarget.classList.add('hidden')
    }
    if (this.hasSpecificStaffTarget) {
      this.specificStaffTarget.classList.add('hidden')
    }
    if (this.hasAllStaffMessageTarget) {
      this.allStaffMessageTarget.classList.add('hidden')
    }
  }

  /**
   * Gets the currently selected audience value
   */
  getSelectedAudience() {
    // First check for a hidden field (when audience is locked)
    const hiddenAudience = this.element.querySelector('input[type="hidden"][name*="communication_audience"]')
    if (hiddenAudience && hiddenAudience.value) {
      return hiddenAudience.value
    }

    // Otherwise check radio buttons
    if (!this.hasAudienceRadioTarget) return null

    const checked = this.audienceRadioTargets.find(radio => radio.checked)
    return checked ? checked.value : null
  }

  /**
   * Gets the currently selected send_to_whom value
   */
  getSelectedSendToWhom() {
    if (!this.hasSendToWhomRadioTarget) return null

    const checked = this.sendToWhomRadioTargets.find(radio => radio.checked)
    return checked ? checked.value : null
  }

  /**
   * Check if any send_to_whom option is selected
   */
  hasSelectedSendToWhom() {
    if (!this.hasSendToWhomRadioTarget) return false
    return this.sendToWhomRadioTargets.some(radio => radio.checked)
  }

  /**
   * Auto-selects the default send_to_whom radio for the given audience.
   * Parents -> all_parents, Staff -> all_staff
   */
  autoSelectDefault(audience) {
    if (!this.hasSendToWhomRadioTarget) return

    const defaultValue = audience === 'school_parents' ? 'all_parents' : 'all_staff'

    // Uncheck all send_to_whom radios first
    this.sendToWhomRadioTargets.forEach(radio => { radio.checked = false })

    // Check the default one
    const target = this.sendToWhomRadioTargets.find(radio => radio.value === defaultValue)
    if (target) {
      target.checked = true
    }
  }
}
